import { formatIndianMobile } from '@oneapp/shared';
import { type ReactNode, type Ref, useEffect, useId, useRef, useState } from 'react';
import { Icon, type IconName } from './icons';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * A body that scrolls with nothing focusable in it can't be scrolled from a keyboard (WCAG 2.1.1), so it
 * takes focus itself then, as Chrome's scrollers do on their own and Safari's don't.
 */
function useKeyboardScroll() {
  const ref = useRef<HTMLDivElement>(null);
  const [focusable, setFocusable] = useState(false);
  useEffect(() => {
    const body = ref.current;
    if (!body || typeof ResizeObserver === 'undefined') return;
    const check = () => setFocusable(body.scrollHeight > body.clientHeight && !body.querySelector(FOCUSABLE));
    const resized = new ResizeObserver(check);
    resized.observe(body);
    const changed = new MutationObserver(check);
    changed.observe(body, { childList: true, subtree: true });
    check();
    return () => {
      resized.disconnect();
      changed.disconnect();
    };
  }, []);
  return { ref, tabIndex: focusable ? 0 : undefined };
}

/** A full-screen page: bar with back/close, scrolling body, and a footer for the main button. */
export function Page({
  bar,
  back,
  action,
  children,
  footer,
  labelledBy,
}: {
  bar: string;
  back?: { label: string; onClick: () => void; icon?: 'chevron-left' | 'x' };
  /** A button at the bar's end ("Mark all read"). */
  action?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  labelledBy?: string;
}) {
  const scroll = useKeyboardScroll();
  return (
    <div className="frame">
      <section className="page" aria-labelledby={labelledBy}>
        <div className="page__bar">
          {back ? (
            <button type="button" className="iconbtn" aria-label={back.label} onClick={back.onClick}>
              <Icon name={back.icon ?? 'chevron-left'} size={22} />
            </button>
          ) : null}
          <p className="page__bar-title">{bar}</p>
          {action ?? null}
        </div>
        <div className="page__body" ref={scroll.ref} tabIndex={scroll.tabIndex}>
          {children}
        </div>
        {footer ? <div className="page__foot">{footer}</div> : null}
      </section>
    </div>
  );
}

export function PageTitle({ id, title, children }: { id?: string; title: string; children?: ReactNode }) {
  return (
    <div className="page__heading">
      <h1 className="page__title" id={id}>
        {title}
      </h1>
      {children ? <p className="page__lede">{children}</p> : null}
    </div>
  );
}

/**
 * Indian mobile number with a fixed +91. Accepts it however it's typed or pasted
 * ("+91 98450-12321", "09845012321") and shows it grouped as "98450 12321".
 */
export function PhoneField({
  label,
  countryLabel,
  digits,
  onChange,
  error,
  inputRef,
}: {
  label: string;
  countryLabel: string;
  digits: string;
  onChange: (digits: string) => void;
  error?: string | undefined;
  inputRef?: Ref<HTMLInputElement>;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        <span className="visually-hidden">{`, ${countryLabel}`}</span>
      </label>
      <div className="phone">
        <span className="phone__prefix" aria-hidden="true">
          +91
        </span>
        <input
          ref={inputRef}
          id={id}
          className="input phone__input"
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          value={formatIndianMobile(digits)}
          onChange={(event) => {
            let next = event.target.value.replace(/\D/g, '');
            if (next.length > 10 && next.startsWith('91')) next = next.slice(2);
            else if (next.length > 10 && next.startsWith('0')) next = next.slice(1);
            onChange(next.slice(0, 10));
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        />
      </div>
      {error ? (
        <p id={errorId} className="field__help field__help--error">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Six boxes for a one-time code. A single real input sits over them, so typing, paste and the
 * phone's "fill in code" suggestion all work; the boxes only show what's in it.
 */
export function OtpInput({
  value,
  onChange,
  onComplete,
  label,
  error = false,
  describedBy,
  disabled = false,
  inputRef,
  length = 6,
  letters = false,
}: {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  label: string;
  error?: boolean;
  describedBy?: string | undefined;
  disabled?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  length?: number;
  /** Letters as well as digits (EVC codes): a keyboard with letters, and capitals as they're typed. */
  letters?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div className={`otp${error ? ' otp--error' : ''}`}>
      <input
        ref={inputRef}
        className="otp__input"
        type="text"
        inputMode={letters ? 'text' : 'numeric'}
        autoComplete="one-time-code"
        autoCapitalize={letters ? 'characters' : undefined}
        autoCorrect={letters ? 'off' : undefined}
        spellCheck={letters ? false : undefined}
        pattern={letters ? '[A-Za-z0-9]*' : '[0-9]*'}
        value={value}
        disabled={disabled}
        aria-label={label}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(event) => {
          const next = event.target.value.replace(letters ? /[^A-Za-z0-9]/g : /\D/g, '').slice(0, length);
          onChange(next);
          if (next.length === length && next !== value) onComplete?.(next);
        }}
        // Keep the caret at the end: the boxes fill left to right.
        onSelect={(event) => {
          const input = event.currentTarget;
          const end = input.value.length;
          if (input.selectionStart !== end || input.selectionEnd !== end) input.setSelectionRange(end, end);
        }}
      />
      <div className="otp__boxes" aria-hidden="true">
        {Array.from({ length }, (_, i) => {
          const digit = value[i] ?? '';
          const active = focused && !error && i === Math.min(value.length, length - 1);
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: the boxes are positions, not items
            <span key={i} className={`otp__box${digit ? ' otp__box--filled' : ''}${active ? ' otp__box--active' : ''}`}>
              {digit}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** English / हिंदी switch. `wide`: the two halves fill the row (a page's own switch, not the corner one). */
export function LanguageToggle({
  value,
  onChange,
  label,
  wide = false,
}: {
  value: 'en' | 'hi';
  onChange: (value: 'en' | 'hi') => void;
  label: string;
  wide?: boolean;
}) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: a group of toggle buttons, not a form fieldset
    <div className={`segmented${wide ? ' segmented--wide' : ''}`} role="group" aria-label={label}>
      <button type="button" aria-pressed={value === 'en'} onClick={() => onChange('en')} lang="en">
        English
      </button>
      <button type="button" aria-pressed={value === 'hi'} onClick={() => onChange('hi')} lang="hi">
        हिंदी
      </button>
    </div>
  );
}

export function Checkbox({
  checked,
  onChange,
  children,
  error,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
  error?: string | undefined;
}) {
  const id = useId();
  return (
    <div className="field">
      <label className={`consent${error ? ' consent--error' : ''}`} htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <span>{children}</span>
      </label>
      {error ? (
        <p id={`${id}-error`} className="field__help field__help--error">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string; lang?: string }[];
  onChange: (value: T) => void;
}) {
  const id = useId();
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <select id={id} className="input select" value={value} onChange={(event) => onChange(event.target.value as T)}>
        {options.map((option) => (
          <option key={option.value} value={option.value} lang={option.lang}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** A tappable row in a settings list (menu, account pages). */
export function SettingsRow({
  icon,
  label,
  value,
  detail,
  badge,
  onClick,
  danger = false,
  busy = false,
}: {
  icon: IconName;
  label: string;
  /** Short, on the right (e.g. "English"). */
  value?: string;
  /** Longer, on a line of its own under the label (e.g. the current chat's name). */
  detail?: string;
  /** Something that needs them, on the right (e.g. "1 overdue"). */
  badge?: { tone: 'red' | 'amber' | 'green' | 'navy'; text: string };
  onClick: () => void;
  danger?: boolean;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      className={`settings__row${danger ? ' settings__row--danger' : ''}`}
      onClick={onClick}
      disabled={busy}
      aria-busy={busy || undefined}
    >
      {busy ? <span className="spinner spinner--dark" aria-hidden="true" /> : <Icon name={icon} size={20} />}
      <span className="settings__label">
        {label}
        {detail ? <span className="settings__detail">{detail}</span> : null}
      </span>
      {value ? <span className="settings__value">{value}</span> : null}
      {badge ? <span className={`badge badge--${badge.tone}`}>{badge.text}</span> : null}
      {danger ? null : <Icon name="chevron-right" size={18} />}
    </button>
  );
}
